import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Plus, Loader2, Check, Trash2, Pencil, X } from "lucide-react";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";

const EMPTY = { nome: "", cognome: "", telefono: "", email: "", compenso: "" };
import { eur, nomeCompleto } from "@/lib/format";

export default function ReferentiManager() {
  const qc = useQueryClient();
  const { data: referenti } = useQuery({ queryKey: ["referenti"], queryFn: async () => (await api.get("/referenti")).data });
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const inp = "w-full px-2.5 py-1.5 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]";
  const reset = () => { setForm(EMPTY); setEditId(null); };

  const save = async () => {
    if (!form.nome.trim() || !form.telefono.trim()) return toast.error("Nome e telefono sono obbligatori");
    setBusy(true);
    try {
      if (editId) await api.put(`/referenti/${editId}`, form);
      else await api.post("/referenti", form);
      await qc.invalidateQueries({ queryKey: ["referenti"] });
      qc.invalidateQueries({ queryKey: ["clienti"] });
      toast.success(editId ? "Referente aggiornato" : "Referente creato");
      reset();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const startEdit = (r) => { setEditId(r.id); setForm({ nome: r.nome || "", cognome: r.cognome || "", telefono: r.telefono || "", email: r.email || "", compenso: r.compenso || "" }); };

  const doDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/referenti/${toDelete.id}`);
      await qc.invalidateQueries({ queryKey: ["referenti"] });
      qc.invalidateQueries({ queryKey: ["clienti"] });
      toast.success("Referente eliminato");
      setToDelete(null);
      if (editId === toDelete.id) reset();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setDelBusy(false); }
  };

  return (
    <div className="bg-white border border-[#1A2B4A]/10 rounded-sm p-4 mt-6" data-testid="referenti-manager">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-4 h-4 text-[#C9A84C]" />
        <h3 className="font-serif text-xl text-[#1A2B4A]">Referenti / Segnalatori</h3>
      </div>
      <p className="text-sm text-[#4a5568] mb-4">Rubrica di chi presenta i clienti (parenti, presentatori…). In assenza il cliente è attribuito a RB Studio.</p>

      <div className="grid sm:grid-cols-4 gap-2 items-end mb-4">
        <div><label className="text-[11px] text-[#4a5568]">Nome *</label>
          <input data-testid="rm-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className={inp} /></div>
        <div><label className="text-[11px] text-[#4a5568]">Cognome</label>
          <input data-testid="rm-cognome" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} className={inp} /></div>
        <div><label className="text-[11px] text-[#4a5568]">Telefono *</label>
          <input data-testid="rm-telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className={inp} /></div>
        <div><label className="text-[11px] text-[#4a5568]">Email</label>
          <input data-testid="rm-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></div>
      </div>
      <div className="mb-4">
        <label className="text-[11px] text-[#4a5568]">Compenso / Provvigione concordata (facoltativo)</label>
        <input data-testid="rm-compenso" value={form.compenso} onChange={(e) => setForm({ ...form, compenso: e.target.value })}
          placeholder="es. 10% · €50 a pratica · da concordare" className={inp} />
      </div>
      <div className="flex gap-2 mb-5">
        <button data-testid="rm-save" onClick={save} disabled={busy}
          className="inline-flex items-center gap-1.5 text-sm bg-[#1A2B4A] text-white px-4 py-2 rounded-sm hover:bg-[#22345c] disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />)} {editId ? "Aggiorna" : "Aggiungi referente"}
        </button>
        {editId && <button data-testid="rm-cancel" onClick={reset} className="inline-flex items-center gap-1 text-sm border border-[#1A2B4A]/20 px-3 rounded-sm hover:border-[#C9A84C]"><X className="w-4 h-4" /> Annulla</button>}
      </div>

      {(referenti || []).length === 0 ? (
        <p className="text-sm text-[#4a5568]">Nessun referente in rubrica.</p>
      ) : (
        <ul className="divide-y divide-[#1A2B4A]/10">
          {(referenti || []).map((r) => (
            <li key={r.id} data-testid={`rm-item-${r.id}`} className="flex items-center justify-between py-2.5 gap-3">
              <div className="min-w-0">
                <div className="text-sm text-[#1A2B4A] font-medium truncate">{nomeCompleto(r)}</div>
                <div className="text-xs text-[#4a5568] truncate">{r.telefono}{r.email ? ` · ${r.email}` : ""}</div>
                <div className="text-[11px] text-[#4a5568] mt-0.5 flex flex-wrap gap-x-2">
                  <span>{r.clienti_count} client{r.clienti_count === 1 ? "e" : "i"}</span>
                  <span>· {r.pratiche_count} pratic{r.pratiche_count === 1 ? "a" : "he"}</span>
                  <span>· onorari <b className="text-[#1A2B4A]">{eur(r.onorari_totali)}</b></span>
                  <span>· incassato <span className="text-[#276749]">{eur(r.incassato)}</span></span>
                </div>
                {r.compenso && <div className="text-[11px] text-[#8a6d1f] mt-0.5">Compenso: {r.compenso}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button data-testid={`rm-edit-${r.id}`} onClick={() => startEdit(r)} className="p-1.5 rounded-md text-[#1A2B4A] hover:bg-[#f9f8f5]" title="Modifica"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`rm-delete-${r.id}`} onClick={() => setToDelete(r)} className="p-1.5 rounded-md text-[#9b2c2c] hover:bg-[#f7e9e9]" title="Elimina"><Trash2 className="w-4 h-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="rm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-[#1A2B4A]">Eliminare il referente?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{toDelete && [toDelete.cognome, toDelete.nome].filter(Boolean).join(" ")}</b> verrà eliminato. I {toDelete?.clienti_count || 0} clienti collegati torneranno a RB Studio.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rm-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="rm-delete-confirm" onClick={(e) => { e.preventDefault(); doDelete(); }} disabled={delBusy} className="bg-[#9b2c2c] hover:bg-[#7a2222]">
              {delBusy && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
